import {
  getGuardrailDecisionDisplay,
  type ApprovalGroup,
  type ApprovalStep,
  type GuardrailCheck,
  type GuardrailDecision,
  type GuardrailEvaluation,
  type GuardrailResult,
} from "src/components/RequisitionGuardrailOutcome";
import {
  withRuleLabels,
  type RuleLabel,
} from "src/components/guardrailRuleLabels";

export type InboxItemType = "REQUISITION_APPROVAL";

export type InboxTypeFilter = "EVERYTHING" | "APPROVALS";

export type InboxPerson = {
  aggregateId?: string | null;
  firstName?: string | null;
  surname?: string | null;
  preferredName?: string | null;
};

export type InboxRequisition = GuardrailResult & {
  externalId?: string | null;
  role?: string | null;
  fte?: number | null;
  location?: string | null;
  openedDate?: string | null;
  targetStartDate?: string | null;
  justification?: string | null;
  supplyTeam?: { id?: string | null; name?: string | null } | null;
  hiringManager?: InboxPerson | null;
};

export type InboxItem = {
  key: string;
  type: InboxItemType;
  requisitionId: string;
  approverGroupId: string;
  title: string;
  subtitle: string;
  step: string;
  flags: { pastSla: boolean; template?: string };
  elapsedHours: number | null;
  clockStartedAt: string | null;
  openedDate: string | null;
  failingChecks: string[];
  subject: { kind: "requisition"; requisition: InboxRequisition } | null;
};

export type QueueRow = {
  id: string;
  approverGroupId: string;
  approverGroupName?: string | null;
  elapsedHours?: number | null;
  clockStartedAt?: string | null;
  pastSla?: boolean | null;
  requisition?: InboxRequisition | null;
};

const SEP = "~";

export const inboxItemKey = (
  requisitionId: string,
  approverGroupId: string
): string =>
  `REQUISITION_APPROVAL${SEP}${encodeURIComponent(requisitionId)}${SEP}${encodeURIComponent(approverGroupId)}`;

export const parseInboxItemKey = (
  key: string
): { requisitionId: string; approverGroupId: string } | null => {
  const parts = key.split(SEP);
  if (parts.length !== 3 || parts[0] !== "REQUISITION_APPROVAL") {
    return null;
  }
  try {
    const requisitionId = decodeURIComponent(parts[1] ?? "");
    const approverGroupId = decodeURIComponent(parts[2] ?? "");
    if (requisitionId === "" || approverGroupId === "") {
      return null;
    }
    return { requisitionId, approverGroupId };
  } catch {
    return null;
  }
};

const text = (value: string | null | undefined): string => value?.trim() ?? "";

export const personLabel = (person: InboxPerson | null | undefined): string => {
  if (!person) {
    return "";
  }
  const given = text(person.preferredName) || text(person.firstName);
  return [given, text(person.surname)].filter(Boolean).join(" ");
};

const fteLabel = (fte: number | null | undefined): string => {
  if (typeof fte !== "number" || !Number.isFinite(fte)) {
    return "";
  }
  return `${fte} FTE`;
};

const failingEffects = (
  checks: GuardrailCheck[] | null | undefined
): GuardrailCheck[] =>
  (checks ?? []).filter((check) => {
    if (check.state === "PASS") {
      return false;
    }
    const effect = check.effect ?? "BLOCK";
    return effect === "BLOCK" || effect === "WARN";
  });

/** Rule name, else the rule id. The detail header and the check row match. */
export const failingCheckLabels = (
  checks: GuardrailCheck[] | null | undefined
): string[] =>
  failingEffects(checks).flatMap((check) => {
    const label = text(check.name) || text(check.ruleId);
    return label ? [label] : [];
  });

/** The list card keeps the check sentence. A missing sentence falls back to the id. */
const failingCheckSentences = (
  checks: GuardrailCheck[] | null | undefined
): string[] =>
  failingEffects(checks).flatMap((check) => {
    const label = text(check.detail) || text(check.ruleId);
    return label ? [label] : [];
  });

export const labelInboxRequisition = (
  requisition: InboxRequisition,
  rules: RuleLabel[]
): InboxRequisition => {
  const evaluation = requisition.guardrailEvaluation;
  if (!evaluation) {
    return requisition;
  }
  const next = withRuleLabels(evaluation, rules);
  if (!next || next === evaluation) {
    return requisition;
  }
  return { ...requisition, guardrailEvaluation: next };
};

const approvalGroups = (
  steps: ApprovalStep[] | null | undefined
): ApprovalGroup[] =>
  (steps ?? []).flatMap((step) =>
    step.kind === "group" ? (step.groups ?? []) : []
  );

export const approvalStatus = (
  steps: ApprovalStep[] | null | undefined,
  decision?: GuardrailDecision | null,
  template?: GuardrailEvaluation["template"]
): {
  label: string;
  variant: "warning" | "success" | "danger" | "primary";
} => {
  const groups = approvalGroups(steps);
  if (groups.length === 0) {
    const display = getGuardrailDecisionDisplay(decision, template);
    return display ?? { label: "In approval", variant: "warning" };
  }
  if (
    groups.some(
      (group) => group.status === "PENDING" || text(group.status) === ""
    )
  ) {
    return { label: "In approval", variant: "warning" };
  }
  if (groups.some((group) => group.status === "REFUSED")) {
    return { label: "Refused", variant: "danger" };
  }
  if (groups.some((group) => group.status === "APPROVED")) {
    return { label: "Approved", variant: "success" };
  }
  return { label: "In approval", variant: "warning" };
};

/** The footer form is one group. A hard block never gets a write button. */
export const decisionTarget = (
  steps: ApprovalStep[] | null | undefined,
  approverGroupId: string,
  decision: string | null | undefined
): { order: number; group: ApprovalGroup } | null => {
  if (decision !== "PASS" || text(approverGroupId) === "") {
    return null;
  }
  const ordered = [...(steps ?? [])].sort(
    (left, right) => left.order - right.order
  );
  for (const step of ordered) {
    if (step.kind !== "group") {
      continue;
    }
    for (const group of step.groups ?? []) {
      if (
        group.approverGroupId === approverGroupId &&
        group.canRespond === true
      ) {
        return { order: step.order, group };
      }
    }
  }
  return null;
};

const templateOf = (
  requisition: InboxRequisition | null | undefined
): string | undefined => {
  const template = requisition?.guardrailEvaluation?.template;
  if (typeof template !== "string") {
    return undefined;
  }
  const trimmed = template.trim();
  return trimmed === "" ? undefined : trimmed;
};

const groupName = (
  requisition: InboxRequisition | null | undefined,
  approverGroupId: string,
  fallback: string | null | undefined
): string => {
  const named = text(fallback);
  if (named) {
    return named;
  }
  for (const step of requisition?.guardrailEvaluation?.steps ?? []) {
    for (const group of step.groups ?? []) {
      if (
        group.approverGroupId === approverGroupId &&
        text(group.approverGroupName)
      ) {
        return text(group.approverGroupName);
      }
    }
  }
  return "Approver group";
};

export const toInboxItem = (row: QueueRow): InboxItem => {
  const requisition = row.requisition ?? null;
  const template = templateOf(requisition);
  const title =
    text(requisition?.role) ||
    text(requisition?.externalId) ||
    text(row.id) ||
    "Requisition";
  const subtitle = [
    text(requisition?.supplyTeam?.name),
    fteLabel(requisition?.fte),
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    key: inboxItemKey(row.id, row.approverGroupId),
    type: "REQUISITION_APPROVAL",
    requisitionId: row.id,
    approverGroupId: row.approverGroupId,
    title,
    subtitle,
    step: groupName(requisition, row.approverGroupId, row.approverGroupName),
    flags: {
      pastSla: row.pastSla === true,
      ...(template ? { template } : {}),
    },
    elapsedHours:
      typeof row.elapsedHours === "number" && Number.isFinite(row.elapsedHours)
        ? row.elapsedHours
        : null,
    clockStartedAt: row.clockStartedAt ?? null,
    openedDate: requisition?.openedDate ?? null,
    failingChecks: failingCheckSentences(
      requisition?.guardrailEvaluation?.checks
    ),
    subject: requisition ? { kind: "requisition", requisition } : null,
  };
};

const timeOf = (value: string | null): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const compareTime = (left: number | null, right: number | null): number => {
  if (left === right) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  return left - right;
};

/** Past SLA first, then oldest clock, then oldest opened date. */
export const compareInboxItems = (
  left: InboxItem,
  right: InboxItem
): number => {
  if (left.flags.pastSla !== right.flags.pastSla) {
    return left.flags.pastSla ? -1 : 1;
  }
  const clock = compareTime(
    timeOf(left.clockStartedAt),
    timeOf(right.clockStartedAt)
  );
  if (clock !== 0) {
    return clock;
  }
  return compareTime(timeOf(left.openedDate), timeOf(right.openedDate));
};

export const sortInboxItems = (items: readonly InboxItem[]): InboxItem[] =>
  [...items].sort(compareInboxItems);

export const matchesTypeFilter = (
  item: InboxItem,
  filter: InboxTypeFilter
): boolean => {
  switch (filter) {
    case "EVERYTHING":
      return true;
    case "APPROVALS":
      switch (item.type) {
        case "REQUISITION_APPROVAL":
          return true;
        default: {
          const unexpected: never = item.type;
          return unexpected;
        }
      }
    default: {
      const unexpected: never = filter;
      return unexpected;
    }
  }
};

const stripPendingReason = (step: ApprovalStep): ApprovalStep => {
  if (step.kind !== "reason" || step.status !== "PENDING") {
    return step;
  }
  const { status, ...rest } = step;
  return rest;
};

const readOnlySteps = (
  steps: ApprovalStep[] | null | undefined
): ApprovalStep[] =>
  (steps ?? []).map((step) => {
    const withoutPendingReason = stripPendingReason(step);
    const groups = withoutPendingReason.groups;
    if (!groups) {
      return withoutPendingReason;
    }
    return {
      ...withoutPendingReason,
      groups: groups.map((group) => ({
        ...group,
        canRespond: false,
        canUndo: false,
      })),
    };
  });

/** A deep link that is no longer the caller's row. Hide approve, refuse, and undo. */
export const readOnlyRequisition = (
  requisition: InboxRequisition
): InboxRequisition => {
  const evaluation = requisition.guardrailEvaluation;
  if (!evaluation) {
    return requisition;
  }
  return {
    ...requisition,
    guardrailEvaluation: {
      ...evaluation,
      steps: readOnlySteps(evaluation.steps),
    },
  };
};
